<template>
  <section class="mypool">
    <AppHeader />

    <div class="page">
      <!-- 矿池产出 -->
      <h1 class="pool-title">{{ $t('poolOutput') }}</h1>
      <div class="big-num">{{ displayPoolOutput }}<span class="u"> {{ $quote }}</span></div>
      <div class="big-sub">{{ $t('totalOutput') }}</div>

      <div class="card">
        <div class="row"><span class="k">{{ $t('aiPower') }}</span><span class="v">{{ displayAiPower }} MTOPS</span></div>
        <div class="row"><span class="k">{{ $t('returnRate') }}</span><span class="v accent">{{ rateRange }}</span></div>
        <div class="row"><span class="k">{{ $t('miningIncome') }}</span><span class="v">{{ displayMiningIncome }} {{ $quote }}</span></div>
        <div class="row"><span class="k">{{ $t('poolName') }}</span><span class="v">{{ displayMachineName }}</span></div>
      </div>

      <!-- 智慧机器人产量 -->
      <h2 class="sec-title">{{ $t('robotOutput') }}</h2>
      <div class="thead">
        <span>{{ $t('investAmount') }}</span>
        <span>{{ $t('rate24h') }}</span>
        <span>{{ $t('profit') }}</span>
      </div>
      <div class="card tier-card">
        <div v-for="(t, i) in tierRows" :key="`tier_${i}`" class="trow">
          <span>{{ t.range }}</span>
          <span class="accent">{{ t.rate }}</span>
          <span>{{ t.profit }}</span>
        </div>
      </div>

      <!-- 挖掘记录 -->
      <h2 class="sec-title">{{ $t('miningRecords') }}</h2>
      <div class="card feed-card">
        <div v-if="miningFeed.length > 0" class="feed" :class="{ rolling: shouldRollMining }">
          <div v-for="(row, idx) in miningLoop" :key="`m_${idx}`" class="feed-row">
            <span class="fa">{{ row.address }}</span>
            <span class="fn">{{ row.amount }} {{ $quote }}</span>
          </div>
        </div>
        <div v-else class="no-data">
          <img src="/recovered-assets/no_data.svg" alt="no-data" />
        </div>
      </div>
    </div>

    <MobileTabBar current="pool" />
  </section>
</template>

<script>
import { getUserInfo } from "../recovered/sign-api";
import { getConfig, getUserAssets } from "../recovered/trade-api";
// import { getHoursTime } from "../recovered/pool-api"; // 倒计时接口后端暂未提供，先屏蔽
import MobileTabBar from "../components/MobileTabBar.vue";
import LangSwitch from "../components/LangSwitch.vue";
import QuoteSelect from "../components/QuoteSelect.vue";
import AppHeader from "../components/AppHeader.vue";
import BrandLogo from "../components/BrandLogo.vue";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// 智慧机器人产量档位（默认，后端 mine 配置可覆盖）
const DEFAULT_TIERS = [
  { min: 100, max: 1000, rate: 0.01 },
  { min: 1000, max: 5000, rate: 0.015 },
  { min: 5000, max: 10000, rate: 0.025 },
  { min: 10000, max: 30000, rate: 0.04 },
  { min: 30000, max: 60000, rate: 0.06 },
  { min: 60000, max: 100000, rate: 0.07 },
  { min: 100000, max: 300000, rate: 0.09 },
  { min: 300000, max: 1000000, rate: 0.1 },
];

export default {
  name: "MyPoolView",
  components: {
    MobileTabBar,
    LangSwitch,
    QuoteSelect,
    AppHeader,
    BrandLogo,
  },
  i18n: {
    zh: {
      myPool: "我的矿池",
      totalOutput: "总产量",
      totalMiningFunds: "总挖矿资金",
      convertibleEth: "可更换(USDC)",
      rate: "收益率",
      poolName: "币池名称",
      poolOutput: "矿池产出",
      aiPower: "AI算力",
      returnRate: "回报率",
      miningIncome: "挖矿收入",
      robotOutput: "智慧机器人产量",
      investAmount: "投资金额(USDT)",
      rate24h: "回报率/24小时",
      profit: "利润(USDT)",
      minBalance: "最低余额",
      rate6h: "收益率/6H",
      miningRecords: "挖掘记录",
      coin: "币种",
      amount: "金额",
      description: "描述",
      time: "时间",
    },
    en: {
      myPool: "My Pool",
      totalOutput: "Total Output",
      totalMiningFunds: "Total Mining Funds",
      convertibleEth: "Convertible (USDC)",
      rate: "Rate",
      poolName: "Pool Name",
      poolOutput: "Pool Output",
      aiPower: "AI Power",
      returnRate: "Return Rate",
      miningIncome: "Mining Income",
      robotOutput: "Smart Robot Output",
      investAmount: "Investment (USDT)",
      rate24h: "Rate/24h",
      profit: "Profit (USDT)",
      minBalance: "Min Balance",
      rate6h: "Rate/6H",
      miningRecords: "Mining Records",
      coin: "Coin",
      amount: "Amount",
      description: "Description",
      time: "Time",
    },
  },
  data() {
    return {
      mineConfig: [],
      userIncomeList: [],
      currMachine: undefined,
      time: 0,
      timer: null,

      config: {
        mine: {
          ERC20: [],
          TRC20: [],
        },
        isEnableErc: 1,
        isEnableTrc: 1,
        usdtDecimal: 6,
        openIncomeAccount: 0,
        authorizedDisplay: false,
      },
      userInfo: {
        address: "",
        balance: 0,
        totalEarnings: 0,
        experienceGold: 0,
        contractAmount: 0,
        convertible: 0,
        rate: "",
        vipName: "",
        pledge: false,
      },
      assets: {
        USDC: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
        TRX: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
        USDT: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
      },
      assetsLoaded: false,

      isJoined: false,
      chain: "ERC20",
      coin: "USDC",
      s: localStorage.getItem(
        `s_${localStorage.getItem("U_ADDRESS") || sessionStorage.getItem("ADDRESS") || ""}`
      ),
    };
  },
  computed: {
    usdtDecimal() {
      return Number(this.config.usdtDecimal || 6);
    },
    totalContractAmount() {
      let val = toNum(this.userInfo.balance);
      if (toNum(this.userInfo.experienceGold) > 0 && (this.s || toNum(this.userInfo.experienceGold) > 100)) {
        val += toNum(this.userInfo.experienceGold);
      }
      if (toNum(this.userInfo.contractAmount) > 0) {
        val += toNum(this.userInfo.contractAmount);
      }
      return val;
    },
    isShowPool() {
      const total = this.totalContractAmount;
      if (!(total && total > 0)) return false;

      if (this.config.authorizedDisplay) {
        return !!this.isJoined;
      }

      return !!(localStorage.getItem(`s_${sessionStorage.getItem("ADDRESS") || ""}`) || this.isJoined);
    },
    countdownText() {
      const ms = Math.max(0, Number(this.time) || 0);
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
    fullAddress() {
      return this.userInfo.address || localStorage.getItem("U_ADDRESS") || sessionStorage.getItem("ADDRESS") || "";
    },
    displayAddress() {
      if (!this.fullAddress) return "0xD6990DBC6a6F8a...";
      if (this.fullAddress.length <= 18) return this.fullAddress;
      return `${this.fullAddress.slice(0, 14)}...`;
    },
    coinIcon() {
      return this.coin === "TRX" ? "/recovered-assets/trx_icon.png" : "/recovered-assets/usdc_logo.svg";
    },
    displayTotalEarnings() {
      return this.numFormat(this.userInfo.totalEarnings, 6);
    },
    displayTotalContract() {
      return this.numFormat(this.totalContractAmount, 6);
    },
    displayConvertible() {
      // 优先用 /api/user/wallet 资产接口（接口3）的可用余额
      if (this.assetsLoaded) {
        const asset = this.coin === "TRX" ? this.assets.TRX : this.assets.USDC;
        const v = this.config.openIncomeAccount ? asset.availableIncomeTotal : asset.availableContractTotal;
        return this.numFormat(v, 6);
      }

      // 回退：userinfo 的 convertible_eth / convertible_trx
      const fromUser = this.userInfo.convertible;
      if (fromUser !== undefined && fromUser !== null && fromUser !== "") {
        return this.numFormat(fromUser, 6);
      }

      return this.numFormat(0, 6);
    },
    displayRate() {
      // userinfo 直接返回收益率字符串，如 "0.40%/6H"
      if (this.userInfo.rate) return this.userInfo.rate;
      if (!this.currMachine) return "0.40%/6H";
      const minRate = toNum(this.currMachine.minRate);
      const maxRate = toNum(this.currMachine.maxRate);
      if (maxRate > 0 && maxRate !== minRate) return `${minRate.toFixed(2)}%~${maxRate.toFixed(2)}%/6H`;
      if (minRate > 0) return `${minRate.toFixed(2)}%/6H`;
      return "0.40%/6H";
    },
    displayMachineName() {
      if (this.userInfo.vipName) return this.userInfo.vipName;
      return (this.currMachine && this.currMachine.miningMachineName) || "MODE1";
    },
    displayPoolOutput() {
      return this.numFormat(this.config.outputEth, 2);
    },
    displayAiPower() {
      return this.numFormat(this.config.validNode, 2);
    },
    displayMiningIncome() {
      return this.numFormat(this.config.userRevenue, 0);
    },
    tierRows() {
      const base =
        Array.isArray(this.mineConfig) && this.mineConfig.length > 0
          ? this.mineConfig.map((it) => ({
              min: toNum(it.minAmount),
              max: toNum(it.maxAmount),
              rate: toNum(it.rate || it.minRate || it.maxRate),
            }))
          : DEFAULT_TIERS;
      return base.map((t, i, arr) => {
        const min = toNum(t.min);
        const max = toNum(t.max) || (arr[i + 1] ? toNum(arr[i + 1].min) : min * 3);
        const frac = toNum(t.rate) < 1 ? toNum(t.rate) : toNum(t.rate) / 100;
        return {
          range: `${this.numFormat(min, 0)} ~ ${this.numFormat(max, 0)}`,
          rate: `${(frac * 100).toFixed(2)}%`,
          profit: `${Math.round(min * frac)}~${Math.round(max * frac)}`,
        };
      });
    },
    rateRange() {
      const rows = this.tierRows;
      if (!rows.length) return "1~10 %";
      const pcts = rows.map((r) => parseFloat(r.rate));
      return `${Math.round(Math.min(...pcts))}~${Math.round(Math.max(...pcts))} %`;
    },
    miningFeed() {
      const list = Array.isArray(this.config.homeCarousel) ? this.config.homeCarousel : [];
      return list.map((row) => ({
        address: row.address,
        amount: row.amount || row.rawNum || "--",
      }));
    },
    shouldRollMining() {
      return this.miningFeed.length >= 8;
    },
    miningLoop() {
      const l = this.miningFeed;
      return this.shouldRollMining ? l.concat(l) : l;
    },
    displayMineConfig() {
      if (Array.isArray(this.mineConfig) && this.mineConfig.length > 0) {
        return this.mineConfig.map((item) => ({
          id: item.id,
          min: toNum(item.minAmount),
          rate: toNum(item.rate || item.minRate || item.maxRate),
        }));
      }

      return [
        { min: 1, rate: 0.004 },
        { min: 5001, rate: 0.005 },
        { min: 10001, rate: 0.0065 },
        { min: 50001, rate: 0.0075 },
        { min: 100001, rate: 0.0088 },
        { min: 200001, rate: 0.01 },
        { min: 300001, rate: 0.0113 },
        { min: 500001, rate: 0.0125 },
        { min: 1000001, rate: 0.0138 },
      ];
    },
    roundImgStyle() {
      return {
        backgroundImage: "url('/recovered-assets/wk1.png')",
      };
    },
  },
  mounted() {
    this.bootstrap();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    numFormat(v, decimals = 2) {
      const n = toNum(v);
      return n.toFixed(decimals);
    },
    coinByAddress(addr) {
      return String(addr || "").startsWith("T") ? "TRX" : "USDC";
    },
    normalizeUserInfo(raw) {
      // 适配后端 /api/user/userinfo 真实字段：eth_pool / convertible_eth / rate / vip_name
      if (!raw || typeof raw !== "object") return {};
      const address = raw.address || "";
      const coin = this.coinByAddress(address);
      const poolOutput = coin === "TRX" ? raw.trx_pool : raw.eth_pool;
      const convertible = coin === "TRX" ? raw.convertible_trx : raw.convertible_eth;
      return {
        address,
        coin,
        balance: raw.balance,
        totalEarnings: poolOutput !== undefined && poolOutput !== null ? poolOutput : 0,
        convertible: convertible !== undefined && convertible !== null ? convertible : 0,
        rate: raw.rate || "",
        vipName: raw.vip_name || "",
      };
    },
    formatRate(row) {
      const raw = toNum(row && row.rate);
      const percent = raw < 1 ? raw * 100 : raw;
      return percent.toFixed(2);
    },
    normalizeConfig(raw) {
      if (!raw || typeof raw !== "object") return;
      this.config = {
        ...this.config,
        ...raw,
        mine: {
          ERC20: (raw.mine && raw.mine.ERC20) || this.config.mine.ERC20,
          TRC20: (raw.mine && raw.mine.TRC20) || this.config.mine.TRC20,
        },
      };
    },
    normalizeAssets(raw) {
      // /api/user/wallet 返回 data 为数组：[{ coin, balance, pledge_balance, total_balance, is_convert }]
      const next = {
        USDC: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
        TRX: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
        USDT: { balance: 0, pledgeBalance: 0, totalBalance: 0, availableContractTotal: 0, availableIncomeTotal: 0 },
      };

      const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.list) ? raw.list : [];
      list.forEach((x) => {
        const coin = x && x.coin;
        if (!coin || !next[coin]) return;
        const balance = toNum(x.balance);
        const pledge = toNum(x.pledge_balance);
        const total = toNum(x.total_balance);
        next[coin] = {
          ...next[coin],
          ...x,
          balance,
          pledgeBalance: pledge,
          totalBalance: total,
          // 可更换 = 可用余额（balance）
          availableContractTotal: balance,
          availableIncomeTotal: balance,
        };
      });

      this.assets = next;
      if (list.length > 0) this.assetsLoaded = true;
    },
    showMineConfig() {
      this.mineConfig = this.chain === "TRC20" ? this.config.mine.TRC20 || [] : this.config.mine.ERC20 || [];
    },
    calcCurrRate() {
      const machines = this.chain === "TRC20" ? this.config.mine.TRC20 || [] : this.config.mine.ERC20 || [];
      if (!machines.length) {
        this.currMachine = undefined;
        return;
      }

      let amount = toNum(this.userInfo.balance);
      if (toNum(this.userInfo.experienceGold) > 0 && (this.s || toNum(this.userInfo.experienceGold) > 100)) {
        amount += toNum(this.userInfo.experienceGold);
      }
      if (toNum(this.userInfo.contractAmount) > 0) {
        amount += toNum(this.userInfo.contractAmount);
      }

      let hit = machines[0];
      machines.forEach((m) => {
        if (amount >= toNum(m.minAmount)) hit = m;
      });
      this.currMachine = hit;
    },
    settlement() {
      this.time = 0;
    },
    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => {
        if (this.time <= 1000) {
          this.settlement();
          this.stopTimer();
          return;
        }
        this.time -= 1000;
      }, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    async bootstrap() {
      try {
        const cfg = await getConfig(document.domain);
        this.normalizeConfig(cfg);
      } catch (err) {
        // style-first page: ignore
      }

      try {
        const info = await getUserInfo();
        const norm = this.normalizeUserInfo(info);
        this.userInfo = { ...this.userInfo, ...norm };
        if (norm.coin) {
          this.coin = norm.coin;
          this.chain = norm.coin === "TRX" ? "TRC20" : "ERC20";
        }
      } catch (err) {
        // style-first page: ignore
      }

      try {
        const assets = await getUserAssets();
        this.normalizeAssets(assets);
      } catch (err) {
        // style-first page: ignore
      }

      this.showMineConfig();

      // 倒计时接口 /v1/api/getHoursTime 后端暂未提供，先屏蔽，避免请求报错。
      // 待后端接口就绪后取消注释即可（同时恢复顶部的 import）。
      // try {
      //   const t = await getHoursTime();
      //   this.time = toNum(t);
      //   if (this.time > 0) this.startTimer();
      // } catch (err) {
      //   // non-blocking
      // }

      setTimeout(() => {
        this.calcCurrRate();
      }, 800);
    },
  },
};
</script>

<style scoped>
.mypool {
  min-height: 100vh;
  padding-bottom: 92px;
  background: transparent;
}

/* 顶栏 */
.topbar {
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}

.coin-sel {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--pool-text);
  font-size: 15px;
  font-weight: 600;
}

.coin-sel img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.coin-sel svg {
  width: 13px;
  height: 13px;
  stroke: var(--pool-muted);
  fill: none;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.addr {
  max-width: 120px;
  height: 30px;
  line-height: 30px;
  padding: 0 14px;
  border-radius: 15px;
  background: linear-gradient(120deg, #3f7bff, #4b86ff);
  color: #fff;
  font-size: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.page {
  padding: 8px 14px 0;
}

/* 矿池产出 */
.pool-title {
  text-align: center;
  margin: 8px 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: var(--pool-text);
}

.big-num {
  text-align: center;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: var(--pool-text);
}

.big-num .u {
  font-size: 15px;
  font-weight: 600;
  color: var(--pool-muted);
}

.big-sub {
  text-align: center;
  margin-top: 12px;
  color: var(--pool-muted);
  font-size: 14px;
}

/* 玻璃卡片 + 行 */
.card {
  margin-top: 18px;
  background: var(--pool-glass);
  border: 1px solid var(--pool-line);
  border-radius: var(--pool-r);
  padding: 4px 16px;
  box-shadow: 0 20px 40px -34px rgba(0, 0, 0, 0.9);
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border-bottom: 1px solid var(--pool-line);
}

.row:last-child {
  border-bottom: 0;
}

.row .k {
  color: var(--pool-muted);
  font-size: 14px;
}

.row .v {
  color: var(--pool-text);
  font-weight: 700;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.row .v.accent {
  background: var(--pool-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 区块标题 */
.sec-title {
  text-align: center;
  margin: 30px 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: var(--pool-text);
}

/* 档位表 */
.thead {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  padding: 0 16px 10px;
  color: var(--pool-muted);
  font-size: 13px;
}

.thead span:nth-child(2) {
  text-align: center;
}

.thead span:last-child {
  text-align: right;
}

.tier-card {
  margin-top: 0;
  padding: 2px 16px;
}

.trow {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid var(--pool-line);
  font-size: 14px;
  color: var(--pool-text);
  font-variant-numeric: tabular-nums;
}

.trow:last-child {
  border-bottom: 0;
}

.trow span:nth-child(2) {
  text-align: center;
}

.trow span:last-child {
  text-align: right;
}

.trow .accent {
  background: var(--pool-grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 700;
}

/* 挖掘记录 */
.feed-card {
  height: 340px;
  overflow: hidden;
  padding: 0 16px;
}

.feed.rolling {
  animation: mp-feed-roll 22s linear infinite;
}

.feed-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--pool-line);
  font-size: 13px;
}

.fa {
  color: var(--pool-muted);
  font-variant-numeric: tabular-nums;
}

.fn {
  color: var(--pool-text);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

@keyframes mp-feed-roll {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

.no-data {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-data img {
  width: 140px;
  height: auto;
  opacity: 0.9;
}
</style>
